import { STORAGE_KEYS } from "@utils/storageKeys";

const DEFAULT_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Timezone used for exact table timestamps — matches Settings → Timezone. */
export function getDisplayTimezone(): string {
  if (typeof window === "undefined") {
    return DEFAULT_TIMEZONE;
  }
  return localStorage.getItem(STORAGE_KEYS.TIMEZONE) ?? DEFAULT_TIMEZONE;
}

/** Short label for column headers, e.g. "CDT" or "GMT+2". */
export function getTimezoneShortLabel(timeZone: string = getDisplayTimezone()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "short",
    }).formatToParts(new Date());
    return parts.find((part) => part.type === "timeZoneName")?.value ?? timeZone;
  } catch {
    return timeZone;
  }
}

/** Append the active timezone short name to a date column label. */
export function withTimezoneLabel(label: string): string {
  return `${label} (${getTimezoneShortLabel()})`;
}

/**
 * Format an instant as YYYY-MM-DD HH:mm in the display timezone.
 * Returns null when the value is missing or invalid (never invents a date).
 */
export function formatExactDateTime(
  value: Date | string | null | undefined,
  timeZone: string = getDisplayTimezone(),
): string | null {
  if (value == null || value === "") {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(date);

    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)?.value ?? "";

    const year = get("year");
    const month = get("month");
    const day = get("day");
    let hour = get("hour");
    const minute = get("minute");

    // en-CA 24h can still emit "24" for midnight in some engines
    if (hour === "24") {
      hour = "00";
    }

    if (!year || !month || !day || !hour || !minute) {
      return null;
    }

    return `${year}-${month}-${day} ${hour}:${minute}`;
  } catch {
    return null;
  }
}
